import React, { useState } from "react";
import styles from "./BetTableCard32B.module.css";
import "./style.css";

// Info tooltip component with collapse
const InfoTooltip = ({ min, max, tooltipId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const formatLimit = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value?.toFixed(1) || "100.0";
  };

  return (
    <div className={styles.infoBlock} onClick={(e) => e.stopPropagation()}>
      <a
        href=""
        data-toggle="collapse"
        data-target={`#${tooltipId}`}
        aria-expanded={isOpen}
        className={`${styles.infoIcon} ${isOpen ? '' : 'collapsed'}`}
        onClick={toggleTooltip}
      >
        <i className="fas fa-info-circle"></i>
      </a>
      <div
        id={tooltipId}
        className={`${styles.minMaxInfo} collapse ${isOpen ? 'show' : ''}`}
      >
        <span className={styles.mr5}>
          <b>Min:</b> <span>{formatLimit(min)}</span>
        </span>
        <span className={styles.mr5}>
          <b>Max:</b> <span>{formatLimit(max)}</span>
        </span>
      </div>
    </div>
  );
};

export default function BetTableCard32B({ data = [], onBetClick, exposures = {}, myBets = [] }) {
  // Helper function to find bet data by nat (name)
  const findBetData = (nat) => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;
    for (const subArray of data) {
      if (Array.isArray(subArray) && subArray.length > 0) {
        const found = subArray.find(item => item?.nat === nat);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to get exposure value - handle various key formats
  const getExposure = (betName) => {
    if (!exposures || Object.keys(exposures).length === 0) return null;

    const keys = [
      betName,
      betName?.toLowerCase(),
      betName?.trim(),
      betName?.replace(/\s+/g, ' ').trim(),
    ];

    for (const key of keys) {
      if (key && exposures[key] !== undefined) {
        return exposures[key];
      }
    }
    return null;
  };

  // Helper function to check if suspended
  const isSuspended = (betData) => {
    return betData?.gstatus?.toUpperCase() === "SUSPENDED" || !betData || betData.b === 0 || betData.b === null || betData.b === undefined;
  };

  // Format exposure value
  const formatExposure = (value) => {
    if (value === null || value === undefined) return "0";
    if (value === 0) return "0";
    // For large numbers, show without decimals
    if (Math.abs(value) >= 1000) {
      return value < 0 ? value.toFixed(0) : value.toFixed(0);
    }
    return value < 0 ? value.toFixed(2) : value.toFixed(2);
  };

  // Extract players (Player 8, 9, 10, 11)
  const players = [8, 9, 10, 11].map(id => {
    const betData = findBetData(`Player ${id}`);
    return {
      id,
      betData,
      back: betData?.b || "0",
      lay: betData?.l || "0",
      isSuspended: isSuspended(betData),
    };
  });

  // Extract Odd/Even bets
  const oddEvenBets = [];
  for (let id = 8; id <= 11; id++) {
    const oddBet = findBetData(`Player ${id} Odd`);
    const evenBet = findBetData(`Player ${id} Even`);
    oddEvenBets.push({
      id,
      oddBet,
      evenBet,
      oddValue: oddBet?.b || "0",
      evenValue: evenBet?.b || "0",
      isOddSuspended: isSuspended(oddBet),
      isEvenSuspended: isSuspended(evenBet),
    });
  }

  // Extract combinations
  const combinations = [
    { name: "Any Three Card Black" },
    { name: "Any Three Card Red" },
    { name: "Two Black Two Red" },
  ].map(combo => {
    const betData = findBetData(combo.name);
    return {
      ...combo,
      betData,
      back: betData?.b || "0",
      lay: betData?.l || "0",
      isSuspended: isSuspended(betData),
    };
  });

  // Extract totals
  const totals = [
    { name: "8 & 9 Total" },
    { name: "10 & 11 Total" },
  ].map(total => {
    const betData = findBetData(total.name);
    return {
      ...total,
      betData,
      odds: betData?.b || "0",
      isSuspended: isSuspended(betData),
    };
  });

  // Extract single bets
  const singleNumbers = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
  const singleBets = singleNumbers.map(num => {
    const betData = findBetData(`Single ${num}`);
    return {
      number: num,
      betData,
      odds: betData?.b || "0",
      isSuspended: isSuspended(betData),
    };
  });

  // Get first player bet data for min/max (assuming all have same limits)
  const firstPlayerBet = players[0]?.betData;

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', width: '100%', gap: '10px' }}>
        {/* Top Row: Players BACK/LAY (Left) and Players EVEN/ODD (Right) */}
        <div style={{ flex: 1 }} className={`row ${styles.row5} m-t-10`}>
          {/* Top-Left: Players Back/Lay */}
          <div className={`col-md-6 col-12 ${styles.mainMarket}`}>
            <div className={`table-responsive ${styles.mainMarket}`}>
              <div className={styles.livePoker}>
                <table className={`table m-b-0 table-bordered`}>
                  <thead style={{ backgroundColor: '#333d79ff', background: '#333d79ff', padding: '2px' }}>
                    <tr>
                      <th className={`${styles.box6} ${styles.headerDarkBlue}`} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                        <div className={styles.infoBlock}>
                          <InfoTooltip
                            min={firstPlayerBet?.min || 100}
                            max={firstPlayerBet?.max || 100000}
                            tooltipId="min-max-info1"
                          />
                        </div>
                      </th>
                      <th className={`text-center ${styles.back} ${styles.box2}`} style={{ backgroundColor: '#72bbef', background: '#72bbef', fontSize: '10px', color: '#000', fontWeight: 'bold', padding: '2px' }}>BACK</th>
                      <th className={`text-center ${styles.lay} ${styles.box2}`} style={{ backgroundColor: '#faa9ba', background: '#faa9ba', fontSize: '10px', color: '#000', fontWeight: 'bold', padding: '2px' }}>LAY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player) => {
                      const exposure = getExposure(`Player ${player.id}`);
                      const isBackLocked = player.isSuspended || !player.back || player.back === "0" || player.back === 0;
                      const isLayLocked = player.isSuspended || !player.lay || player.lay === "0" || player.lay === 0;

                      return (
                        <tr key={player.id} className={`${styles.betInfo} ${player.isSuspended ? styles.suspendedTr : ""}`}>
                          <td className={styles.box6} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                            <p className="m-b-0" style={{ color: 'white', fontSize: '12px', marginLeft: '10px' }}><b>Player {player.id}</b></p>
                            <p className="m-b-0" style={{ color: exposure !== null && exposure > 0 ? 'red' : 'white', fontSize: '10px', marginLeft: '10px' }}>
                              <b>{formatExposure(exposure)}</b>
                            </p>
                          </td>
                          <td
                            className={`text-center back ${styles.box2} ${isBackLocked ? styles.suspended : ""}`}
                            onClick={() => !isBackLocked && onBetClick && onBetClick(player.back, `Player ${player.id}`, "back", player.betData)}
                            style={{
                              cursor: isBackLocked ? 'not-allowed' : 'pointer',
                              backgroundColor: isBackLocked ? 'rgba(217, 220, 223)' : '#72bbef',
                              background: isBackLocked ? 'rgba(217, 220, 223)' : '#72bbef',
                              padding: '2px'
                            }}
                          >
                            <div style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }} className={`${styles.odd} d-block`}>
                              {isBackLocked ? "0" : (player.back || "0")}
                            </div>
                            <div className="d-block" style={{
                              color: exposure !== null && exposure > 0 ? 'red' : 'black',
                              fontSize: '10px',
                            }}>
                              {formatExposure(exposure)}
                            </div>
                          </td>
                          <td
                            className={`text-center lay ${styles.box2} ${isLayLocked ? styles.suspended : ""}`}
                            onClick={() => !isLayLocked && player.lay && onBetClick && onBetClick(player.lay, `Player ${player.id}`, "lay", player.betData)}
                            style={{
                              cursor: isLayLocked ? 'not-allowed' : 'pointer',
                              backgroundColor: isLayLocked ? 'rgba(217, 220, 223)' : '#faa9ba',
                              background: isLayLocked ? 'rgba(217, 220, 223)' : '#faa9ba',
                              padding: '2px'
                            }}
                          >
                            <div style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }} className={`${styles.odd} d-block`}>
                              {isLayLocked || !player.lay ? "0" : player.lay}
                            </div>
                            <div className="d-block" style={{
                              fontSize: '10px',
                              color: exposure !== null && exposure > 0 ? 'red' : 'black',
                            }}>
                              {formatExposure(exposure)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top-Right: Players Even/Odd */}
          <div className={`col-md-6 col-12 ${styles.mainMarket}`}>
            <div className={`table-responsive ${styles.mainMarket}`}>
              <div className={styles.livePoker}>
              

                <table className={`table m-b-0 table-bordered`}>
                  <thead style={{ backgroundColor: '#333d79ff', background: '#333d79ff', padding: '2px' }}>
                    <tr>
                      <th className={`${styles.box6} ${styles.headerDarkBlue}`} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                        <div className={styles.infoBlock}>
                          <InfoTooltip
                            min={combinations[0]?.betData?.min || 100}
                            max={combinations[0]?.betData?.max || 100000}
                            tooltipId="min-max-info3"
                          />
                        </div>
                      </th>
                      <th className={`text-center back ${styles.box2}`} style={{ backgroundColor: '#72bbef', background: '#72bbef', fontSize: '10px', color: '#000', fontWeight: 'bold', padding: '2px' }}>BACK</th>
                      <th className={`text-center lay ${styles.box2}`} style={{ backgroundColor: '#faa9ba', background: '#faa9ba', fontSize: '10px', color: '#000', fontWeight: 'bold', padding: '2px' }}>LAY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinations.map((combo, index) => {
                      const exposure = getExposure(combo.name);
                      const isBackLocked = combo.isSuspended || !combo.back || combo.back === "0" || combo.back === 0;
                      const isLayLocked = combo.isSuspended || !combo.lay || combo.lay === "0" || combo.lay === 0;

                      return (
                        <tr key={index} className={`${styles.betInfo} ${combo.isSuspended ? styles.suspendedTr : ""}`}>
                          <td className={styles.box6} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                            <p className="m-b-0" style={{ color: 'white', fontSize: '12px', marginLeft: '10px' }}><b>{combo.name}</b></p>
                            <p className="m-b-0" style={{ color: exposure !== null && exposure > 0 ? 'red' : 'white', fontSize: '10px', marginLeft: '10px' }}>
                              {formatExposure(exposure)}
                            </p>
                          </td>
                          <td
                            className={`text-center back ${styles.box2} ${isBackLocked ? styles.suspended : ""}`}
                            onClick={() => !isBackLocked && onBetClick && onBetClick(combo.back, combo.name, "back", combo.betData)}
                            style={{
                              cursor: isBackLocked ? 'not-allowed' : 'pointer',
                              backgroundColor: isBackLocked ? 'rgba(217, 220, 223)' : '#72bbef',
                              background: isBackLocked ? 'rgba(217, 220, 223)' : '#72bbef',
                              padding: '2px'
                            }}
                          >
                            <div style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }} className={`${styles.odd} d-block`}>
                              {isBackLocked ? "0" : (combo.back || "0")}
                            </div>
                            <div className="d-block" style={{
                              fontSize: '10px',
                              color: exposure !== null && exposure > 0 ? 'red' : 'black',
                            }}>
                              {formatExposure(exposure)}
                            </div>
                          </td>
                          <td
                            className={`text-center lay ${styles.box2} ${isLayLocked ? styles.suspended : ""}`}
                            onClick={() => !isLayLocked && combo.lay && onBetClick && onBetClick(combo.lay, combo.name, "lay", combo.betData)}
                            style={{
                              cursor: isLayLocked ? 'not-allowed' : 'pointer',
                              backgroundColor: isLayLocked ? 'rgba(217, 220, 223)' : '#faa9ba',
                              background: isLayLocked ? 'rgba(217, 220, 223)' : '#faa9ba',
                              padding: '2px'
                            }}
                          >
                            <span className={`${styles.odd} d-block`}>
                              <b>{isLayLocked || !combo.lay ? "0" : combo.lay}</b>
                            </span>
                            <span className="d-block" style={{
                              color: exposure !== null && exposure > 0 ? 'red' : 'black',
                              fontSize: '10px'
                            }}>
                              {formatExposure(exposure)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Card Combinations (Left) and Totals (Right) */}
        <div style={{ flex: 1 }} className={`row ${styles.row5} m-t-10`}>
          {/* Bottom-Left: Card Combinations */}
          <div className={`col-md-6 col-12 ${styles.mainMarket}`}>
            <div className={`table-responsive ${styles.mainMarket}`}>
              <div className={styles.livePoker}>
              <table className={`table m-b-0 table-bordered`}>
                  <thead style={{ backgroundColor: '#333d79ff', background: '#333d79ff', padding: '2px' }}>
                    <tr>
                      <th className={`${styles.box6} ${styles.headerDarkBlue}`} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                        <div className={styles.infoBlock}>
                          <InfoTooltip
                            min={oddEvenBets[0]?.oddBet?.min || 100}
                            max={oddEvenBets[0]?.oddBet?.max || 100000}
                            tooltipId="min-max-info2"
                          />
                        </div>
                      </th>
                      <th className={`text-center back ${styles.box2}`} style={{ backgroundColor: '#72bbef', background: '#72bbef', fontSize: '10px', color: '#000', fontWeight: 'bold', padding: '2px' }}>EVEN</th>
                      <th className={`text-center back ${styles.box2}`} style={{ backgroundColor: '#72bbef', background: '#72bbef', fontSize: '10px', color: '#000', fontWeight: 'bold', padding: '2px' }}>ODD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oddEvenBets.map((bet) => {
                      const oddExposure = getExposure(`Player ${bet.id} Odd`);
                      const evenExposure = getExposure(`Player ${bet.id} Even`);

                      return (
                        <tr key={bet.id} className={styles.betInfo}>
                          <td className={styles.box6} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                            <p className="m-b-0" style={{ color: 'white', fontSize: '12px', marginLeft: '10px' }}><b>Player {bet.id}</b></p>
                            <p className="m-b-0" style={{ color: (oddExposure !== null && oddExposure > 0) || (evenExposure !== null && evenExposure > 0) ? 'red' : 'white', fontSize: '10px', marginLeft: '10px' }}>
                              <b>{formatExposure(oddExposure || evenExposure || 0)}</b>
                            </p>
                          </td>
                          <td
                            className={`text-center back ${styles.box2} ${bet.isEvenSuspended || !bet.evenValue || bet.evenValue === "0" || bet.evenValue === 0 ? styles.suspended : ""}`}
                            onClick={() => !bet.isEvenSuspended && bet.evenValue && bet.evenValue !== "0" && bet.evenValue !== 0 && onBetClick && onBetClick(bet.evenValue, `Player ${bet.id} Even`, "back", bet.evenBet)}
                            style={{
                              cursor: (bet.isEvenSuspended || !bet.evenValue || bet.evenValue === "0" || bet.evenValue === 0) ? 'not-allowed' : 'pointer',
                              backgroundColor: (bet.isEvenSuspended || !bet.evenValue || bet.evenValue === "0" || bet.evenValue === 0) ? 'rgba(217, 220, 223)' : '#72bbef',
                              background: (bet.isEvenSuspended || !bet.evenValue || bet.evenValue === "0" || bet.evenValue === 0) ? 'rgba(217, 220, 223)' : '#72bbef',
                              padding: '2px'
                            }}
                          >
                            <div style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }} className={`${styles.odd} d-block`}>
                              {(bet.isEvenSuspended || !bet.evenValue || bet.evenValue === "0" || bet.evenValue === 0) ? "0" : (bet.evenValue || "0")}
                            </div>
                            <div className="d-block" style={{
                              color: evenExposure !== null && evenExposure > 0 ? 'red' : 'black',
                              fontSize: '10px',
                            }}>
                              {formatExposure(evenExposure)}
                            </div>
                          </td>
                          <td
                            className={`text-center back ${styles.box2} ${bet.isOddSuspended || !bet.oddValue || bet.oddValue === "0" || bet.oddValue === 0 ? styles.suspended : ""}`}
                            onClick={() => !bet.isOddSuspended && bet.oddValue && bet.oddValue !== "0" && bet.oddValue !== 0 && onBetClick && onBetClick(bet.oddValue, `Player ${bet.id} Odd`, "back", bet.oddBet)}
                            style={{
                              cursor: (bet.isOddSuspended || !bet.oddValue || bet.oddValue === "0" || bet.oddValue === 0) ? 'not-allowed' : 'pointer',
                              backgroundColor: (bet.isOddSuspended || !bet.oddValue || bet.oddValue === "0" || bet.oddValue === 0) ? 'rgba(217, 220, 223)' : '#72bbef',
                              background: (bet.isOddSuspended || !bet.oddValue || bet.oddValue === "0" || bet.oddValue === 0) ? 'rgba(217, 220, 223)' : '#72bbef',
                              padding: '2px'
                            }}
                          >
                            <div style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }} className={`${styles.odd} d-block`}>
                              {(bet.isOddSuspended || !bet.oddValue || bet.oddValue === "0" || bet.oddValue === 0) ? "0" : (bet.oddValue || "0")}
                            </div>
                            <div className="d-block" style={{
                              fontSize: '10px',
                              color: oddExposure !== null && oddExposure > 0 ? 'red' : 'black',
                            }}>
                              {formatExposure(oddExposure)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Bottom-Right: Totals */}
          <div className={`col-md-6 col-12 ${styles.cardTotalBlock}`}>
            <div className={`table-responsive ${styles.cardTotalBlock}`}>
              <div className={styles.livePoker}>
                <table className={`table m-b-0 table-bordered`}>
                  <thead style={{ backgroundColor: '#333d79ff', background: '#333d79ff', padding: '2px' }}>
                    <tr>
                      <th colSpan="3" className={`${styles.box10} ${styles.headerDarkBlue}`} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                        <div className={`${styles.infoBlock} ${styles.totalsInfoBlock}`}>
                          <InfoTooltip
                            min={totals[0]?.betData?.min || 100}
                            max={totals[0]?.betData?.max || 100000}
                            tooltipId="min-max-info4"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {totals.map((total, index) => {
                      const exposure = getExposure(total.name);
                      const isLocked = total.isSuspended || !total.odds || total.odds === "0" || total.odds === 0;

                      return (
                        <tr key={index} className={styles.betInfo}>
                          <td className={styles.box6} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                            <p className="m-b-0" style={{ color: 'white', fontSize: '12px', marginLeft: '10px' }}><b>{total.name}</b></p>
                            <p className="m-b-0" style={{ color: exposure !== null && exposure > 0 ? 'red' : 'white', fontSize: '10px', marginLeft: '10px' }}>
                              {formatExposure(exposure)}
                            </p>
                          </td>
                          <td
                            className={`text-center back ${styles.box4} ${isLocked ? styles.suspended : ""}`}
                            onClick={() => !isLocked && onBetClick && onBetClick(total.odds, total.name, "back", total.betData)}
                            style={{
                              cursor: isLocked ? 'not-allowed' : 'pointer',
                              backgroundColor: isLocked ? 'rgba(217, 220, 223)' : '#72bbef',
                              background: isLocked ? 'rgba(217, 220, 223)' : '#72bbef',
                              padding: '2px'
                            }}
                          >
                            <div style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }} className={`${styles.odd} d-block`}>
                              {isLocked ? "0" : (total.odds || "0")}
                            </div>
                            <div className="d-block" style={{
                              fontSize: '10px',
                              color: exposure !== null && exposure > 0 ? 'red' : 'black',
                            }}>
                              {formatExposure(exposure)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div>
      {/* Row 2: Single Numbers Grid */}
      <div className={`row ${styles.row5} m-t-10`}>
        <div className={`col-12`}>
          <div className={`table-responsive ${styles.ocadaContainer}`}>
            <table className={`${styles.table} ${styles.tableBordered}`}>
              <tbody>
                <tr>
                  <th colSpan="5" className={`${styles.textCenter} ${styles.cardOdds} ${styles.srate}`} style={{ backgroundColor: '#333d79ff', background: '#333d79ff', color: 'white', padding: '2px' }}>
                    {singleBets[0]?.betData?.b || "0"}
                    <div className={`${styles.infoBlock} float-right`}>
                      <InfoTooltip
                        min={singleBets[0]?.betData?.min || 100}
                        max={singleBets[0]?.betData?.max || 25000}
                        tooltipId="min-max-info5"
                      />
                    </div>
                  </th>
                </tr>
                <tr className={styles.back}>
                  {singleBets.slice(0, 5).map((singleBet, index) => {
                    const exposure = getExposure(`Single ${singleBet.number}`);
                    const isLocked = singleBet.isSuspended || !singleBet.odds || singleBet.odds === "0" || singleBet.odds === 0;

                    return (
                      <td
                        key={index}
                        className={`${styles.betAction} ${isLocked ? styles.suspended : ''}`}
                        onClick={() => !isLocked && onBetClick && onBetClick(singleBet.odds, `Single ${singleBet.number}`, "back", singleBet.betData)}
                        style={{ cursor: isLocked ? 'not-allowed' : 'pointer', padding: '2px', backgroundColor: '#72bbef', background: '#72bbef' , border:'1px solid #666', textAlign:'center'}}
                      >
                        <div className={`${styles.dBlock} ${styles.cardNumber}`}>
                          {singleBet.number}
                        </div>
                        <p style={{
                          color: exposure !== null && exposure > 0 ? 'red' : 'black',
                          margin: '4px 0 0 0',
                          fontSize: '12px',
                          fontWeight: 'normal'
                        }}>
                          {formatExposure(exposure)}
                        </p>
                      </td>
                    );
                  })}
                </tr>
                <tr className={styles.back}>
                  {singleBets.slice(5, 10).map((singleBet, index) => {
                    const exposure = getExposure(`Single ${singleBet.number}`);
                    const isLocked = singleBet.isSuspended || !singleBet.odds || singleBet.odds === "0" || singleBet.odds === 0;

                    return (
                      <td
                        key={index}
                        className={`${styles.betAction} ${isLocked ? styles.suspended : ''}`}
                        onClick={() => !isLocked && onBetClick && onBetClick(singleBet.odds, `Single ${singleBet.number}`, "back", singleBet.betData)}
                        style={{ cursor: isLocked ? 'not-allowed' : 'pointer', padding: '2px', backgroundColor: '#72bbef', background: '#72bbef', border:'1px solid #666', textAlign:'center' }}
                      >
                        <div className={`${styles.dBlock} ${styles.cardNumber}`}>
                          {singleBet.number}
                        </div>
                        <p style={{
                          color: exposure !== null && exposure > 0 ? 'red' : 'black',
                          margin: '4px 0 0 0',
                          fontSize: '12px',
                          fontWeight: 'normal'
                        }}>
                          {formatExposure(exposure)}
                        </p>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

